// src/components/common/Footer.jsx
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaLinkedin,
  FaGithub,
  FaTwitter,
  FaMedium,
  FaEnvelope,
} from "react-icons/fa";
import { HiOutlineExternalLink } from "react-icons/hi";
import profileImage from "../../assets/images/profile.jpg";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    projects: [
      {
        name: "VXDF Specification",
        href: "https://github.com/mihir-shah99/vxdf",
        external: true,
      },
      { name: "All Projects", href: "/projects" },
      {
        name: "Open Source",
        href: "https://github.com/mihir-shah99",
        external: true,
      },
    ],
    resources: [
      { name: "My Book", href: "/book" },
      { name: "Blog", href: "/blog" },
      { name: "Talks", href: "/talks" },
    ],
    connect: [
      { name: "About", href: "/" },
      { name: "Contact", href: "/contact" },
      { name: "Testimonials", href: "/testimonials" },
    ],
  };

  const socialLinks = [
    {
      icon: FaLinkedin,
      href: "https://linkedin.com/in/mihir-shah99",
      label: "LinkedIn",
    },
    {
      icon: FaGithub,
      href: "https://github.com/mihir-shah99",
      label: "GitHub",
    },
    { icon: FaTwitter, href: "https://x.com/mihirshah99", label: "Twitter" },
    { icon: FaMedium, href: "https://mihirshah99.medium.com", label: "Medium" },
    { icon: FaEnvelope, href: "mailto:mihir@mihirshah.tech", label: "Email" },
  ];

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-vxdf-dark text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-hero-pattern opacity-5"></div>
      <motion.div
        className="absolute top-0 right-0 w-96 h-96 bg-vxdf-accent/5 rounded-full blur-3xl"
        animate={{ scale: [1, 1.1, 1], rotate: [0, 45, 0] }}
        transition={{ duration: 20, repeat: Infinity }}
      />

      <div className="container mx-auto px-4 py-16 relative z-10">
        {/* Top Section */}
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {/* Brand Column */}
          <div className="md:col-span-1">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full overflow-hidden shadow-lg ring-2 ring-vxdf-accent/20">
                <img
                  src={profileImage}
                  alt="Mihir Shah"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h3 className="font-bold text-lg">Mihir Shah</h3>
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-500/10 text-blue-400 rounded-md border border-blue-500/20">
                    ✍️ Author
                  </span>
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-emerald-500/10 text-emerald-400 rounded-md border border-emerald-500/20">
                    🛡️ Security Leader
                  </span>
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-500/10 text-purple-400 rounded-md border border-purple-500/20">
                    🎤 Speaker
                  </span>
                </div>
              </div>
            </div>
            <p className="text-gray-400 text-sm mb-6">
              Security Engineer, Author, and passionate advocate for 
              cybersecurity excellence and knowledge sharing.
            </p>
          </div>

          {/* Links Columns */}
          <div className="md:col-span-3 grid grid-cols-3 gap-8">
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <h4 className="font-semibold text-lg mb-4 capitalize">
                  {category}
                </h4>
                <ul className="space-y-2">
                  {links.map((link) => (
                    <li key={link.name}>
                      {link.external ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-vxdf-accent transition-colors flex items-center group"
                        >
                          {link.name}
                          <HiOutlineExternalLink
                            className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            size={14}
                          />
                        </a>
                      ) : (
                        <Link
                          to={link.href}
                          className="text-gray-400 hover:text-vxdf-accent transition-colors"
                        >
                          {link.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-800 mb-8"></div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          {/* Social Links */}
          <div className="flex space-x-4">
            {socialLinks.map((social) => (
              <motion.a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 hover:bg-vxdf-secondary flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300"
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                aria-label={social.label}
              >
                <social.icon size={18} />
              </motion.a>
            ))}
          </div>

          {/* Copyright */}
          <div className="text-center md:text-right text-gray-400 text-sm">
            <p className="flex items-center justify-center md:justify-end">
              © {currentYear} Mihir Shah. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
